import axiosInstance from "../../utils/AxiosInstance";
import { toast } from "react-toastify";
import { useAuthHeader, useAuthUser } from "react-auth-kit";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useGetOrganizations } from "../HandlingComponents/";
import { toastError, toastSuccess } from "../../utils/utils";

// GET /patients
export const useGetPatients = () => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const organizationsQuery = useGetOrganizations();

  return useQuery({
    queryKey: ["patients"],
    queryFn: async () => {
      const res = await axiosInstance.get(`/patients`, {
        headers: { Authorization: authHeader() },
      });

      if (authUser()?.role !== 1) return res.data;

      // if the user is superadmin, add organizations column
      let data = res.data;
      let patients = [];

      for (let p of data.patients) {
        let org = organizationsQuery.data?.organizations?.find(
          (org) => org.id === p.org_id
        );

        // add org_name to doctor object
        patients.push({
          ...p,
          org_name: org.name,
        });
      }

      data["patients"] = patients;
      return data;
    },
    enabled: organizationsQuery.isSuccess,
  });
};

// GET /patients/:patient_id
export const useGetPatientById = (patient_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["patients", patient_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/patients/${patient_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: patient_id !== undefined && patient_id !== null,
  });
};

// === PUT /patients/:patient_id ===
export const usePutPatientById = (patient_id) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();
  const getOriginalData = useGetPatientById(patient_id);

  return useMutation({
    mutationFn: async (editedPatient) => {
      if (editedPatient === undefined) return;
      if (
        editedPatient.disclaimer !== undefined &&
        editedPatient.disclaimer !== null
      )
        delete editedPatient.disclaimer;
      const res = await axiosInstance.put(
        `/patients/${patient_id}`,
        editedPatient,
        {
          headers: { Authorization: authHeader() },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["patients", variables.id]); // update views inside ViewDoctor
      queryClient.invalidateQueries(["patients"]); // update views inside Doctors page
      toast.success("Dettagli paziente aggiornati!", toastSuccess);
    },
    onError: (data, variables, context) => {
      queryClient.setQueryData(["patients", patient_id], (oldData) => {
        return {
          ...oldData,
          ...getOriginalData.data,
        };
      });
      toast.error("Dettagli paziente non aggiornati", toastError);
    },
  });
};

// === POST /patients ===
export const useNewPatient = (closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newPatient) => {
      const res = await axiosInstance.post(`/patients`, newPatient, {
        headers: {
          Authorization: authHeader(),
        },
      });
    },
    onSuccess: (data) => {
      // close modal and display toast
      toast.success("Paziente aggiunto con successo!", toastSuccess);
      closeModal();

      queryClient.invalidateQueries(["patients"]); // update views inside Doctors page
    },
    onError: () => {
      // close modal and display error
      toast.error("Errore nell'aggiunta del paziente", toastError);
      closeModal();
    },
  });
};

// === DELETE /patients ===
export const useDeletePatient = () => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patient_id) => {
      const res = await axiosInstance.delete(`/patients/${patient_id}`, {
        headers: {
          Authorization: authHeader(),
        },
      });
    },
    onSuccess: () => {
      toast.success("Paziente dismesso con successo!", toastSuccess);
      queryClient.invalidateQueries(["patients"]); // update views inside Doctors page
    },
    onError: () => {
      toast.error("Errore nella dismissione del paziente", toastError);
    },
  });
};
